"use client";

import { useEffect, useState, useRef } from "react";
import { usePathname } from "next/navigation";
import { Building2, Home, RefreshCw, WifiOff } from "lucide-react";
import Button from "@/components/ui/Button";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";

/**
 * NetworkDetector component that monitors online/offline status
 * and shows an overlay when offline (instead of redirecting, which fails when offline)
 */
export default function NetworkDetector() {
	const pathname = usePathname();
	const [isOffline, setIsOffline] = useState(false);
	const pathnameRef = useRef(pathname);

	// Keep pathname ref in sync
	useEffect(() => {
		pathnameRef.current = pathname;
	}, [pathname]);

	useEffect(() => {
		// Only run on client side
		if (typeof window === "undefined") return;

		// Don't show overlay if already on network error page
		if (pathnameRef.current === "/network-error") {
			return;
		}

		// Check initial online status
		setIsOffline(!navigator.onLine);

		// Handle online event
		const handleOnline = () => {
			setIsOffline(false);
		};

		// Handle offline event
		const handleOffline = () => {
			// Only show overlay if not on network error page
			if (pathnameRef.current !== "/network-error") {
				setIsOffline(true);
			}
		};

		// Add event listeners
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		// Cleanup
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, [pathname]);

	const handleTryAgain = () => {
		if (typeof window !== "undefined") {
			window.location.reload();
		}
	};

	// Don't show overlay on network error page itself
	if (pathname === "/network-error") {
		return null;
	}

	return (
		<AnimatePresence>
			{isOffline && (
				<>
					{/* Backdrop */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0 bg-black/80 z-[9998]"
					/>
					{/* Error Overlay */}
					<motion.div
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.95 }}
						transition={{ duration: 0.3 }}
						className="fixed inset-0 z-[9999] flex items-center justify-center p-6 pointer-events-none"
					>
						<div
							className="w-full max-w-2xl pointer-events-auto"
							style={{ maxWidth: "75%" }}
						>
							{/* Header */}
							<div className="text-center mb-12">
								<div className="flex items-center justify-center gap-2 mb-8">
									<div className="w-12 h-12 rounded-full bg-[#A7FF5E] flex items-center justify-center">
										<Building2 className="h-7 w-7 text-[#16251c]" />
									</div>
									<span className="text-[#edffde] text-2xl tracking-wide">
										Tax Lien Vest
									</span>
								</div>
							</div>

							{/* Error Card */}
							<div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
								{/* Icon */}
								<div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6 bg-[#F3F4F6]">
									<WifiOff className="h-12 w-12 text-[#6B7280]" />
								</div>

								{/* Title */}
								<h1 className="text-3xl mb-3 text-[#16251c] font-bold">
									Network Connection Lost
								</h1>

								{/* Subtitle */}
								<p className="text-xl mb-4 font-semibold text-[#6B7280]">
									Unable to connect to Tax Lien Vest
								</p>

								{/* Description */}
								<p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
									It looks like you&apos;re offline or
									experiencing network issues. Please check
									your internet connection and try again.
								</p>

								{/* Actions */}
								<div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
									<Button
										onClick={handleTryAgain}
										className="bg-[#A7FF5E] text-[#16251c] hover:bg-[#95e84d] h-11 px-6"
									>
										<RefreshCw className="h-4 w-4 mr-2" />
										Try Again
									</Button>

									<Link href="/">
										<Button
											variant="outline"
											className="h-11 px-6 border-gray-300 text-[#16251c] hover:bg-gray-50"
										>
											<Home className="h-4 w-4 mr-2" />
											Back to Home
										</Button>
									</Link>
								</div>

								{/* Additional Help */}
								<div className="mt-8 pt-8 border-t border-gray-200">
									<p className="text-sm text-gray-500">
										Need help?{" "}
										<Link
											href="/contact-us"
											className="text-[#417714] hover:underline"
										>
											Contact Support
										</Link>
									</p>
								</div>
							</div>

							{/* Footer Note */}
							<div className="text-center mt-8">
								<p className="text-xs text-[#edffde]/60">
									Error Code: NETWORK • Tax Lien Vest Platform
								</p>
							</div>
						</div>
					</motion.div>
				</>
			)}
		</AnimatePresence>
	);
}
